import { DATASTRATEGY, EntityManager } from '@dch/entity-manager';
import { SessionStorageStrategy } from '@dch/entity-manager/lib/storage/strategies/session-storage.strategy';
import * as i0 from '@angular/core';
import { Injectable, Pipe } from '@angular/core';
import { map } from 'rxjs/operators';
import * as i1$1 from '@dch/ng-common/src/lib/environment';
import * as i1 from '@dch/ng-common/src/lib/cookie';

const PublicRestDataTranslationsEntity = {
    secure: false,
    requestHeaders: ['language'],
    rel: 'translations',
    url: '/.rest/nodes/v1/publicRestData/webapps/',
    version: 'v1',
    parameters: {},
    dataStrategy: DATASTRATEGY.CACHE_NETWORK_WITH_ERRORS,
    storageStrategy: SessionStorageStrategy.ID,
};
const OTATranslationsEntity = {
    secure: false,
    url: '/.rest/pxquery/v1/translations?app={app}&plat=web&language={language}&v={version}',
    version: 'v1',
    dataStrategy: DATASTRATEGY.CACHE_NETWORK_WITH_ERRORS,
    storageStrategy: SessionStorageStrategy.ID,
};

class LanguageService {
    constructor(cookieService) {
        this.cookieService = cookieService;
    }
    getSelectedLanguage() {
        if (this.language) {
            return this.language;
        }
        this.language = this.cookieService.getCookie('LANGUAGE') || window.language || 'en';
        return this.language;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: LanguageService, deps: [{ token: i1.CookieService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: LanguageService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: LanguageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.CookieService }] });

class TranslationService {
    constructor(environmentService, languageService) {
        this.environmentService = environmentService;
        this.languageService = languageService;
        this.publicRestDataTranslations = {};
    }
    parse(nativeElement, customTranslations) {
        if (!this.translations) {
            customTranslations
                ? (this.translations = this.process(nativeElement.getAttribute(customTranslations)))
                : (this.translations = this.process(nativeElement.getAttribute('translations')));
        }
        else {
            customTranslations
                ? (this.translations = {
                    ...this.translations,
                    ...this.process(nativeElement.getAttribute(customTranslations)),
                })
                : (this.translations = {
                    ...this.translations,
                    ...this.process(nativeElement.getAttribute('translations')),
                });
        }
    }
    getTranslatedValueFor(key, removePTagEnabled) {
        let translationValue = '';
        if (!this.translations[key]) {
            if (!this.environmentService.isProd) {
                console.error(`Missing translation for: ${key}`);
            }
        }
        else {
            translationValue = this.translations[key];
            if (removePTagEnabled) {
                translationValue = this.removeWrappingPTag(translationValue);
            }
        }
        return translationValue;
    }
    getTranslatedValueWithPlaceholderReplaced(key, placeholderValue, removePTagEnabled) {
        return this.getTranslatedValueFor(key, removePTagEnabled).replace(/{placeholder}/g, placeholderValue);
    }
    addTranslationsFromOTAFor(app, context) {
        const params = {
            app,
            language: this.languageService.getSelectedLanguage(),
            version: this.environmentService.isDR || this.environmentService.isProd ? '10.10.10' : '1.1.1',
        };
        const OTAEntity = context
            ? { ...OTATranslationsEntity, url: OTATranslationsEntity.url + '&context=' + context }
            : OTATranslationsEntity;
        return EntityManager.getEntity(OTAEntity, params).pipe(map((otaTranslationsEntity) => {
            if (otaTranslationsEntity.value) {
                this.translations = {
                    ...this.translations,
                    ...this.process(JSON.stringify(otaTranslationsEntity.value)),
                };
                return true;
            }
            return false;
        }));
    }
    addTranslationsFromPublicRestData(key, depth = 20) {
        const translationsEntityConfig = Object.assign({}, PublicRestDataTranslationsEntity);
        translationsEntityConfig.url = this.buildTranslationUrl(translationsEntityConfig.url, key, depth);
        const params = { translationKey: key };
        return EntityManager.getEntity(translationsEntityConfig, params).pipe(map((translationsEntity) => {
            if (translationsEntity.value) {
                this.filterAndAddRequiredTranslationElements(translationsEntity.value, key);
                this.translations = {
                    ...this.translations,
                    ...this.process(JSON.stringify(this.publicRestDataTranslations)),
                };
                return true;
            }
            return false;
        }));
    }
    filterAndAddRequiredTranslationElements(translation, key) {
        if (translation.properties && translation.properties.length > 0) {
            const translationProperties = translation.properties.filter((a) => a.name === this.getUserLanguageProperty());
            if (translationProperties && translationProperties.length > 0) {
                this.publicRestDataTranslations[key] = translationProperties[0].values[0];
            }
        }
        if (translation.nodes && translation.nodes.length > 0) {
            for (const translationNode of translation.nodes) {
                this.filterAndAddRequiredTranslationElements(translationNode, key + '.' + translationNode.name);
            }
        }
    }
    removeEndingLineBreak(translation) {
        if (typeof translation === 'string' && translation.indexOf('\n') === translation.length - 1) {
            return translation.slice(0, -1);
        }
        return translation;
    }
    removeWrappingPTag(translation) {
        if (typeof translation === 'string' &&
            translation.indexOf('<p>') === 0 &&
            translation.lastIndexOf('</p>') === translation.length - 4) {
            translation = translation.substring(3, translation.length - 4);
        }
        return translation;
    }
    process(jsonString) {
        if (!jsonString) {
            return {};
        }
        // FIXME: this is not unit-tested, PhantomJS issue (https://github.com/ariya/phantomjs/issues/11172)
        const htmlEntitiesEscapedString = jsonString.replace(/&#([0-9]{1,3});/gi, (match, numStr) => {
            const num = parseInt(numStr, 10); // read num as normal number
            return String.fromCharCode(num);
        });
        return JSON.parse(htmlEntitiesEscapedString);
    }
    getUserLanguageProperty() {
        let languageProperty = 'value';
        const language = this.languageService.getSelectedLanguage();
        if (language !== 'en') {
            languageProperty += '_' + language;
        }
        return languageProperty;
    }
    buildTranslationUrl(url, key, depth) {
        const translationKeyArray = key.split('.');
        translationKeyArray.forEach((path) => {
            url += path + '/';
        });
        if (depth) {
            url += '?depth=' + depth;
        }
        return url;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TranslationService, deps: [{ token: i1$1.EnvironmentService }, { token: LanguageService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TranslationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TranslationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1$1.EnvironmentService }, { type: LanguageService }] });

class TranslatePipe {
    constructor(translationService) {
        this.translationService = translationService;
    }
    transform(key, removePTagEnabled, placeholder) {
        if (placeholder) {
            return this.translationService.getTranslatedValueWithPlaceholderReplaced(key, placeholder, removePTagEnabled);
        }
        return this.translationService.getTranslatedValueFor(key, removePTagEnabled);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TranslatePipe, deps: [{ token: TranslationService }], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.1", ngImport: i0, type: TranslatePipe, isStandalone: true, name: "translate" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.1", ngImport: i0, type: TranslatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'translate',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: TranslationService }] });

/**
 * Generated bundle index. Do not edit.
 */

export { LanguageService, OTATranslationsEntity, PublicRestDataTranslationsEntity, TranslatePipe, TranslationService };
